import _ from 'lodash';

import { User } from 'ev-types';

import api, { Base, Tags } from 'ev-api/api';
import { sanitizeAndTransformResponse } from 'ev-api/common/transformers';

import {
  GetProviderParams,
  GetProvidersParams,
  ProviderAvailabilityParams,
  ProviderPracticeAvailabilityResponse,
  UpdateProviderAvailabilityParams,
} from './params';
import { getProvidersTransform, getProviderTransform } from './transformers';

const providersApi = api.injectEndpoints({
  endpoints: builder => ({
    getProviders: builder.query<Record<string, User>, GetProvidersParams>({
      query: ({ ids, practiceId = false, query, lite }) => ({
        url: `${Base.V3}/providers/bulk_show`,
        method: 'PUT',
        body: {
          ids: _.uniq(ids),
          practice_id: practiceId,
          query,
          lite,
        },
      }),
      transformResponse: getProvidersTransform,
      providesTags: [Tags.Providers],
    }),
    getProvider: builder.query<User | undefined, GetProviderParams>({
      query: ({ id, practiceId, lite }) => ({
        url: `${Base.V3}/providers/bulk_show`,
        method: 'PUT',
        body: { ids: [id], practice_id: practiceId, lite },
      }),
      transformResponse: getProviderTransform,
    }),
    getProviderPracticeAndGroupAvailabilities: builder.query<
      ProviderPracticeAvailabilityResponse,
      ProviderAvailabilityParams
    >({
      query: ({ providerId }) => ({
        url: `${Base.V3}/providers/${providerId}/availabilities`,
        method: 'GET',
      }),
      transformResponse: sanitizeAndTransformResponse,
      providesTags: [Tags.Availability],
    }),
    makeProviderAvailableInPracticeOrGroup: builder.mutation<
      void,
      UpdateProviderAvailabilityParams
    >({
      query: ({ providerId, practiceId, practiceGroupId }) => ({
        url: `${Base.V3}/providers/${providerId}/availabilities/make_available`,
        method: 'POST',
        body: {
          practice_group_id: practiceGroupId,
          practice_id: practiceId,
        },
      }),
      invalidatesTags: [Tags.Availability],
    }),
    makeProviderUnavailableInPracticeOrGroup: builder.mutation<
      void,
      UpdateProviderAvailabilityParams
    >({
      query: ({
        providerId,
        practiceId,
        practiceGroupId,
        availabilityDiscrepancyCheck,
      }) => ({
        url: `${Base.V3}/providers/${providerId}/availabilities/make_unavailable`,
        method: 'POST',
        body: {
          practice_group_id: practiceGroupId,
          practice_id: practiceId,
          availability_discrepancy_check: availabilityDiscrepancyCheck,
        },
      }),
      invalidatesTags: [Tags.Availability],
    }),
  }),
});

export const {
  useGetProvidersQuery,
  useLazyGetProvidersQuery,
  useLazyGetProviderQuery,
  useGetProviderQuery,
  useGetProviderPracticeAndGroupAvailabilitiesQuery,
  useMakeProviderAvailableInPracticeOrGroupMutation,
  useMakeProviderUnavailableInPracticeOrGroupMutation,
} = providersApi;
